import React, { useState } from 'react';
import { LivePage, FilterBar, DataTable, Age, Code, assertExecOk, clustersAPI , useNamespaces } from './LivePage';
import WorkloadDetailDrawer from '../components/WorkloadDetailDrawer';
import CreateServiceModal from '../components/CreateServiceModal';
import { useCluster } from '../context/ClusterContext';
import { useAuth } from '../context/AuthContext';
import ActionsMenu, { MENU_ITEM } from '../components/ActionsMenu';

export default function Services() {
  const { hasSectionAction } = useAuth();
  const { selectedCluster } = useCluster();
  // SARVKUBE-65: the cluster's REAL namespace list, so the dropdown offers namespaces whose
  // objects are not currently displayed. Built from loaded rows before, which made it
  // impossible to reach a namespace that was not already on screen.
  const { namespaces: allNamespaces } = useNamespaces(selectedCluster?.id);
  const canCreate = hasSectionAction('networking', 'create');
  const canDelete = hasSectionAction('networking', 'delete');
  const [nsFilter, setNsFilter]     = useState('all');
  const [nameFilter, setNameFilter] = useState('');
  // Describe + edit for one object, in the shared drawer.
  const [detailFor, setDetailFor] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  return (
    <>
    <LivePage deps={[nsFilter]} title="Services" fetchFn={id => clustersAPI.getWorkloadPaged('services', id, { pageSize: 500, namespace: nsFilter === 'all' ? undefined : nsFilter })}>
      {(data, refresh, clusterId) => {
        // The API returns a raw K8s List ({items:[Service|Ingress...]}), not a
        // pre-shaped {services:[...]}. Normalize Service items into the flat
        // shape the table expects. (Was `data.items || data.services || []` -> always empty
        // -> "No items found" even though the cluster has services.)
        const _items = data.items || data.services || [];
        const svcs = _items
          .filter(it => (it.kind || 'Service') === 'Service')
          .map(it => {
            if (it.metadata) {  // raw K8s object
              const sp = it.spec || {};
              const ports = (sp.ports || []).map(p =>
                `${p.port}${p.nodePort ? ':' + p.nodePort : ''}/${p.protocol || 'TCP'}`).join(', ');
              const ext = (sp.externalIPs || [])[0]
                || ((it.status?.loadBalancer?.ingress || [])[0]?.ip)
                || (sp.type === 'ExternalName' ? sp.externalName : '');
              return {
                name: it.metadata.name,
                namespace: it.metadata.namespace,
                type: sp.type || 'ClusterIP',
                cluster_ip: sp.clusterIP || '—',
                external_ip: ext || '',
                ports,
                created_at: it.metadata.creationTimestamp,
              };
            }
            return it;  // already flat
          });
        const namespaces = ['all', ...allNamespaces].sort();   // cluster-wide, not just loaded rows
        const filtered = svcs.filter(s =>
          (nsFilter === 'all' || s.namespace === nsFilter) &&
          s.name.toLowerCase().includes(nameFilter.toLowerCase())
        );

        const handleDelete = async (name, namespace) => {
          if (!window.confirm(`Delete service "${name}" in namespace "${namespace}"?`)) return;
          try {
            const r = await clustersAPI.exec(clusterId, `kubectl delete svc ${name} -n ${namespace} --ignore-not-found=true`, { section: 'networking', action: 'delete' });
            assertExecOk(r, 'Delete');   // kubectl failures come back HTTP 200 on stderr
            refresh();
          } catch (e) { alert(`Delete failed: ${e.response?.data?.detail || e.message}`); }
        };

        return <>
          <FilterBar count={filtered.length} total={svcs.length} label="services">
            <select value={nsFilter} onChange={e => setNsFilter(e.target.value)}>
              {namespaces.map(ns => <option key={ns} value={ns}>{ns === 'all' ? 'All namespaces' : ns}</option>)}
            </select>
            <input placeholder="Filter by name..." value={nameFilter} onChange={e => setNameFilter(e.target.value)} />
            {canCreate && (
            <button onClick={() => setShowCreate(true)} className="btn btn-primary btn-sm" style={{ marginLeft:'auto' }}>
              + Create Service
            </button>
            )}
          </FilterBar>
          <DataTable cols={['Name', 'Namespace', 'Type', 'Cluster IP', 'External IP', 'Ports', 'Age', '']}
            rows={filtered.map(s => (
              <tr key={`${s.namespace}/${s.name}`}>
                <td><Code>{s.name}</Code></td>
                <td style={{ color: 'var(--ink-2)', fontSize: 13 }}>{s.namespace}</td>
                <td><span className="role-chip">{s.type}</span></td>
                <td style={{ fontSize: 12 }}>{s.cluster_ip}</td>
                <td style={{ fontSize: 12, color: s.external_ip ? 'var(--color-success-500)' : 'var(--muted)' }}>{s.external_ip || '—'}</td>
                <td style={{ fontSize: 12 }}>{s.ports}</td>
                <td><Age ts={s.created_at} /></td>
                <td>
                  <ActionsMenu>
                    <button onClick={() => setDetailFor({ namespace: s.namespace, name: s.name })}
                      style={MENU_ITEM} title="Selectors, ports, routes, events and editable YAML">
                      Details
                    </button>
                    {canDelete && (
                    <button onClick={() => handleDelete(s.name, s.namespace)} style={{ ...MENU_ITEM, color: 'var(--color-danger-500)' }}>
                      Delete
                    </button>
                    )}
                  </ActionsMenu>
                </td>
              </tr>
            ))} />
          {showCreate && (
            <CreateServiceModal
              clusterId={clusterId}
              initialNamespace={nsFilter !== 'all' ? nsFilter : ''}
              onClose={() => setShowCreate(false)}
              onCreated={() => { setShowCreate(false); refresh(); }}
            />
          )}
        </>;
      }}
    </LivePage>
    {detailFor && selectedCluster && (
      <WorkloadDetailDrawer
        clusterId={selectedCluster.id}
        kind="Service"
        resource="services"
        namespace={detailFor.namespace}
        name={detailFor.name}
        onClose={() => setDetailFor(null)}
      />
    )}
    </>
  );
}
